import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { type DetailedPoliceReport } from '@/services/api/generated/policeReportsService';

import { InvolvedAgencySection } from './InvolvedAgencySection';
import { PoliceReportCard } from './PoliceReportCard';
import { analyzeInvolvedAgency } from './utils/involvedAgencyLogic';

interface PoliceReportsListProps {
  reports: DetailedPoliceReport[];
  matterId: string;
  onReportSelect: (reportId: string) => void;
  firmRequiresFoia?: boolean;
}

export const PoliceReportsList = ({
  reports,
  matterId,
  onReportSelect,
  firmRequiresFoia,
}: PoliceReportsListProps) => {
  const [showAll, setShowAll] = useState(false);
  const summary = analyzeInvolvedAgency(reports);

  const hasSecondary = summary.secondaryReports.length > 0;

  return (
    <div className="space-y-4">
      {/* Primary reports wrapped with agency context */}
      <InvolvedAgencySection summary={summary}>
        {summary.primaryReports.map((report) => (
          <PoliceReportCard
            key={report.id}
            report={report}
            matterId={matterId}
            onSelect={() => onReportSelect(report.id)}
            firmRequiresFoia={firmRequiresFoia}
          />
        ))}
      </InvolvedAgencySection>

      {/* Secondary/minimized reports - collapsible */}
      {hasSecondary && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            {showAll ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
            <span>
              {showAll ? 'Hide' : 'Show'} {summary.secondaryReports.length}{' '}
              other{' '}
              {summary.secondaryReports.length === 1 ? 'agency' : 'agencies'}
            </span>
          </button>

          {showAll && (
            <div className="space-y-3 opacity-50">
              {summary.secondaryReports.map((report) => (
                <PoliceReportCard
                  key={report.id}
                  report={report}
                  matterId={matterId}
                  onSelect={() => onReportSelect(report.id)}
                  firmRequiresFoia={firmRequiresFoia}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
